import React, { useState, useEffect } from 'react';
import { Edit, Close } from '@mui/icons-material';
import api, { roleService, userService } from '../services/api';
import { PillButton } from './ui/Buttons';

interface RoleAssignment {
  id: number;
  userId: number;
  userName: string;
  roleId: number;
  roleName: string;
  managerId?: number;
  managerName?: string;
  assignedAt: string;
}

const RoleAssignments: React.FC = () => {
  const [assignments, setAssignments] = useState<RoleAssignment[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [editingAssignment, setEditingAssignment] = useState<RoleAssignment | null>(null);
  const [showEdit, setShowEdit] = useState(false);
  const [formData, setFormData] = useState({
    roleId: '',
    managerId: ''
  });

  useEffect(() => {
    fetchAssignments();
    fetchUsers();
    fetchRoles();
  }, []);

  const fetchAssignments = async () => {
    try {
      const response = await api.get('/roles/assignments');
      setAssignments(response.data || []);
    } catch (error) {
      console.error('Error fetching assignments:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await userService.getUsers();
      setUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchRoles = async () => {
    try {
      const data = await roleService.getRoles();
      setRoles(data || []);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const handleEdit = (assignment: RoleAssignment) => {
    setEditingAssignment(assignment);
    setFormData({
      roleId: assignment.roleId.toString(),
      managerId: assignment.managerId?.toString() || ''
    });
    setShowEdit(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingAssignment) return;

    try {
      await api.put(`/roles/assignments/${editingAssignment.id}`, {
        roleId: parseInt(formData.roleId),
        managerId: formData.managerId ? parseInt(formData.managerId) : null
      });
      setShowEdit(false);
      setEditingAssignment(null);
      fetchAssignments();
    } catch (error) {
      console.error('Error updating assignment:', error);
    }
  };

  const handleDelete = async (assignmentId: number) => {
    if (window.confirm('Are you sure you want to remove this role assignment?')) {
      try {
        await api.delete(`/roles/assignments/${assignmentId}`);
        fetchAssignments();
      } catch (error) {
        console.error('Error deleting assignment:', error);
      }
    }
  };

  return (
    <div className="role-assignments">
      <h3>Current Role Assignments</h3>
      
      <div className="assignments-table">
        <table>
          <thead>
            <tr>
              <th>Employee</th>
              <th>Role</th>
              <th>Manager</th>
              <th>Assigned Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {assignments.map(assignment => (
              <tr key={assignment.id}>
                <td>{assignment.userName}</td>
                <td>{assignment.roleName}</td>
                <td>{assignment.managerName || 'No Manager'}</td>
                <td>{new Date(assignment.assignedAt).toLocaleDateString()}</td>
                <td>
                  <div className="row-actions">
                    <PillButton variant="warning" size="sm" onClick={() => handleEdit(assignment)}>
                      <Edit sx={{ fontSize: 14 }} /> Edit
                    </PillButton>
                    <PillButton variant="danger" size="sm" onClick={() => handleDelete(assignment.id)}>
                      <Close sx={{ fontSize: 14 }} /> Remove
                    </PillButton>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showEdit && (
        <div className="modal">
          <div className="modal-content">
            <h3>Edit Role Assignment</h3>
            <form onSubmit={handleUpdate}>
              <div className="form-group">
                <label>Employee:</label>
                <input type="text" value={editingAssignment?.userName} disabled />
              </div>
              <div className="form-group">
                <label>Role:</label>
                <select
                  value={formData.roleId}
                  onChange={(e) => setFormData({...formData, roleId: e.target.value})}
                  required
                >
                  {roles.map(role => (
                    <option key={role.id} value={role.id}>{role.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Manager:</label>
                <select
                  value={formData.managerId}
                  onChange={(e) => setFormData({...formData, managerId: e.target.value})}
                >
                  <option value="">No Manager</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>{user.fullName}</option>
                  ))}
                </select>
              </div>
              <div className="form-actions">
                <PillButton type="submit" variant="success">Update</PillButton>
                <PillButton type="button" variant="neutral" onClick={() => setShowEdit(false)}>Cancel</PillButton>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .role-assignments {
          margin-top: 30px;
        }
        .assignments-table {
          overflow-x: auto;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 15px;
        }
        th, td {
          padding: 12px;
          text-align: left;
          border-bottom: 1px solid #e2e8f0;
        }
        th {
          background: #f8fafc;
          font-weight: bold;
        }
        .row-actions {
          display: flex;
          gap: 8px;
        }
        .modal {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(2,6,23,0.6);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }
        .modal-content {
          background: white;
          padding: 20px;
          border-radius: 16px;
          width: 400px;
        }
        .form-group {
          margin-bottom: 15px;
        }
        .form-group label {
          display: block;
          margin-bottom: 5px;
          font-weight: bold;
        }
        .form-group input, .form-group select {
          width: 100%;
          padding: 8px;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
        }
        .form-actions {
          display: flex;
          gap: 10px;
          justify-content: flex-end;
        }
      `}</style>
    </div>
  );
};

export default RoleAssignments;